import React, { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { pricingPlans } from '../mock/data';
import { toast } from 'sonner';

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSelectPlan = (planName) => {
    setSelectedPlan(planName);
    toast.success(`${planName} selected! Scroll down to book your session.`);
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
            Pricing Plans
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Flexible Pricing to Suit Your Needs
          </h2>
          <p className="text-lg text-gray-600">
            Choose the plan that works best for you. All plans include personalized support and comprehensive resources.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingPlans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative transition-all duration-300 ${
                plan.popular 
                  ? 'border-2 border-teal-600 shadow-2xl scale-105 md:scale-110' 
                  : 'border-2 hover:border-teal-200 hover:shadow-xl'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-teal-600 text-white px-4 py-1 flex items-center space-x-1">
                    <Sparkles className="h-3 w-3" />
                    <span>Most Popular</span>
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                {plan.savings && (
                  <Badge variant="outline" className="mb-4 border-green-500 text-green-700">
                    {plan.savings}
                  </Badge>
                )}
                <div className="mt-4">
                  <span className="text-5xl font-bold text-gray-900">£{plan.price}</span>
                  <span className="text-gray-500 ml-2">{plan.period}</span>
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button 
                  className={`w-full py-6 text-lg ${
                    plan.popular 
                      ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  onClick={() => handleSelectPlan(plan.name)}
                >
                  {selectedPlan === plan.name ? 'Selected ✓' : 'Select Plan'}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Not sure which plan is right for you?
            </h3>
            <p className="text-gray-700 mb-4">
              Book a free 15-minute consultation to discuss your needs and goals. 
              We'll recommend the best option for your situation.
            </p>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> All prices are in GBP. Payment accepted via bank transfer, PayPal, or card. 
              First session satisfaction guaranteed or your money back.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
